import cx from "classnames";
import {
  ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useCallback,
  useRef,
  useState,
} from "react";
import styles from "./Toaster.module.scss";
import { ToastProvider, useToasts } from "./ToastProvider";
import {
  ToastCustomEventDetail,
  ToastCustomEventDetailAdd,
  ToastCustomEventDetailUpdate,
  ToastUpdateProps,
} from "./toast-methods";
import { ToastProps } from "./Toast";
import { ANIMATION_DURATION, useCloseToast } from "./toast-utils";
import { useMergeRefs } from "../../hooks";
import { ToastStack, ToastStackProps } from "./ToastStack";
import { ToasterPopover } from "./ToasterPopover";

export type ToastEventType = {
  eventType: "add" | "update" | "dismiss" | "dismiss-all";
  toastCount: number;
  id?: string;
  toasterId?: string;
  toastProps?: ToastProps | ToastUpdateProps;
};

/**
 * Props for the Toaster component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends Omit<ToastStackProps, "toasts">
 */
export type ToasterProps = ComponentPropsWithoutRef<"div"> &
  Omit<ToastStackProps, "toasts"> & {
    /**
     * When `true`, the toaster will not listen for custom events to add, update, or dismiss toasts.
     * Use this to prevent multiple toasters from displaying the same toasts.
     * @default false
     */
    disableEvents?: boolean;
    /**
     * @deprecated This prop has no effect. The toaster no longer conditionally renders internally.
     * @default false
     */
    forceRender?: boolean;
    /**
     * Unique toaster id.
     */
    id: string;
    /**
     * Callback function that is called when toasts are added, updated, or dismissed.
     */
    onToastEvent?: (toastEvent: ToastEventType) => void;
  };

type DialogToasterCustomEventDetail = {
  eventType: "dialog-toaster";
};
type DialogToasterCustomEvent = CustomEvent<DialogToasterCustomEventDetail>;

/**
 * Toaster component for managing and displaying toast notifications.
 *
 * Features:
 * - Manages multiple toast notifications in a stack
 * - Handles toast positioning and stacking behavior
 * - Supports custom events for toast management
 * - Automatic toast dismissal with configurable duration
 * - Keyboard navigation and accessibility support
 * - Dialog-aware positioning for modal contexts
 * - Session storage for dialog toaster state
 * - Custom event system for toast lifecycle management
 * - Responsive design with mobile support
 *
 * @example
 * <Toaster
 *   id="main-toaster"
 *   duration={5000}
 *   toastsBeforeStack={3}
 *   onToastEvent={(event) => console.log('Toast event:', event)}
 * />
 *
 * @example
 * <Toaster
 *   id="dialog-toaster"
 *   disableEvents={false}
 *   focusKey="t"
 * />
 */
export const ToasterContent = forwardRef<HTMLDivElement, ToasterProps>(
  (
    {
      className,
      disableEvents = false,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- pulling out of props
      forceRender = false,
      id,
      onToastEvent,

      // ToastStackProps
      duration,
      focusKey,
      toastsBeforeStack,
      ...props
    },
    ref,
  ) => {
    const toasterRef = useRef<HTMLDivElement>(null);
    const mergedToasterRef = useMergeRefs([toasterRef, ref]);

    const { toasts, setToasts, isDialogToaster } = useToasts();

    // refs using state to be used inside event listeners
    const disableEventsRef = useRef<boolean>(disableEvents);
    const toastCountRef = useRef<number>(0);
    const isDialogToasterRef = useRef<boolean>(isDialogToaster);

    const [shouldHideToaster, setShouldHideToaster] = useState(false);

    const updateDialogToasterStorage = useCallback(
      (isDialogToaster: boolean) => {
        const dialogToasters = JSON.parse(
          window.sessionStorage.getItem("dialog-toasters") ?? "[]",
        );

        if (isDialogToaster) {
          if (!dialogToasters.includes(id)) {
            window.sessionStorage.setItem(
              "dialog-toasters",
              JSON.stringify([...dialogToasters, id]),
            );
          }
        } else if (dialogToasters.includes(id)) {
          window.sessionStorage.setItem(
            "dialog-toasters",
            JSON.stringify(
              dialogToasters.filter(
                (dialogToaster: string) => dialogToaster !== id,
              ),
            ),
          );
        }
      },
      [id],
    );

    useEffect(() => {
      disableEventsRef.current = disableEvents;
    }, [disableEvents]);

    useEffect(() => {
      toastCountRef.current = toasts.length;
    }, [toasts.length]);

    useEffect(() => {
      // update session storage
      updateDialogToasterStorage(isDialogToaster);
      // update ref for event listeners
      isDialogToasterRef.current = isDialogToaster;

      // Dispatch a custom event to notify other Toasters
      const DialogToasterEvent: DialogToasterCustomEvent =
        new CustomEvent<DialogToasterCustomEventDetail>("dialog-toaster", {
          composed: true,
          detail: {
            eventType: "dialog-toaster",
          },
        });
      dispatchEvent(DialogToasterEvent);
      // don't need to check for function change
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDialogToaster]);

    const closeToast = useCloseToast(styles.close);

    useEffect(() => {
      let timeout: NodeJS.Timeout | undefined;
      updateDialogToasterStorage(isDialogToaster);

      /**
       * Listen for custom events to add, update, or dismiss toasts.
       */
      const toastEventListener = (e: Event) => {
        if (disableEventsRef.current) {
          return;
        }

        const { eventType, ...toastProps } = (
          e as CustomEvent<ToastCustomEventDetail>
        ).detail;

        if (eventType === "add") {
          const toastAddProps = toastProps as ToastCustomEventDetailAdd;

          if (!toastAddProps.toasterId || toastAddProps.toasterId === id) {
            setToasts((prev) => [...prev, toastProps as ToastProps]);
          }

          // allow for state change to finish before calling onToastEvent()
          timeout = setTimeout(() => {
            onToastEvent?.({
              eventType: "add",
              toastCount: toastCountRef.current,
              id: toastAddProps.id,
              toasterId: id,
              toastProps: toastAddProps,
            });
          });
        } else if (eventType === "update") {
          const toastUpdateProps = toastProps as ToastCustomEventDetailUpdate;

          setToasts((prev) => {
            return prev.map((t) => {
              if (t.id === toastUpdateProps.id) {
                return {
                  ...t,
                  ...toastUpdateProps,
                };
              } else {
                return t;
              }
            });
          });

          onToastEvent?.({
            eventType: "update",
            toastCount: toastCountRef.current,
            id: toastUpdateProps.id,
            toasterId: id,
            toastProps: toastUpdateProps,
          });
        } else if (eventType === "dismiss") {
          if (toastProps.id) {
            closeToast(toastProps.id);

            // allow for animation to finish before calling onToastEvent()
            timeout = setTimeout(() => {
              onToastEvent?.({
                eventType: "dismiss",
                toastCount: toastCountRef.current,
                id: toastProps.id,
                toasterId: id,
              });
            }, ANIMATION_DURATION);
          } else {
            closeToast();

            onToastEvent?.({
              eventType: "dismiss-all",
              toastCount: 0,
              toasterId: id,
            });
          }
        }
      };

      /**
       * Listen for custom events that indicate a dialog toaster is open or closed.
       */
      const dialogToasterEventListener = (e: Event) => {
        const { eventType } = (e as CustomEvent<DialogToasterCustomEventDetail>)
          .detail;
        if (eventType === "dialog-toaster") {
          const dialogToasters = JSON.parse(
            window.sessionStorage.getItem("dialog-toasters") ?? "[]",
          );
          if (dialogToasters.length > 0) {
            if (
              isDialogToasterRef.current &&
              dialogToasters[dialogToasters.length - 1] === id
            ) {
              setShouldHideToaster(false);
            } else {
              setShouldHideToaster(true);
            }
          } else {
            setShouldHideToaster(false);
          }
        }
      };

      const onPageRefreshListener = () => {
        window.sessionStorage.removeItem("dialog-toasters");
      };

      window.addEventListener("hammer-toast", toastEventListener);
      window.addEventListener("dialog-toaster", dialogToasterEventListener);
      window.addEventListener("beforeunload", onPageRefreshListener);

      return () => {
        window.removeEventListener("hammer-toast", toastEventListener);
        window.removeEventListener(
          "dialog-toaster",
          dialogToasterEventListener,
        );
        window.removeEventListener("beforeunload", onPageRefreshListener);

        clearTimeout(timeout);
        updateDialogToasterStorage(false);
      };
      // only run on first render
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toasterClasses = cx(styles.toaster, {
      [styles["hidden"]]: shouldHideToaster,
      [`${className}`]: className != null,
    });

    return (
      <div
        data-anv="toaster"
        data-toaster-id={id}
        aria-hidden={shouldHideToaster}
        className={toasterClasses}
        ref={mergedToasterRef}
        {...props}
      >
        <ToastStack
          toasts={toasts}
          duration={duration}
          focusKey={focusKey}
          toastsBeforeStack={toastsBeforeStack}
        />
      </div>
    );
  },
);

export const Toaster = forwardRef<HTMLDivElement, ToasterProps>(
  (props, ref) => {
    return (
      <ToastProvider>
        <ToasterPopover>
          <ToasterContent {...props} ref={ref} />
        </ToasterPopover>
      </ToastProvider>
    );
  },
);

Toaster.displayName = "Toaster";
ToasterContent.displayName = "ToasterContent";
